import type React from 'react';
import { Box, Card, FormField, FormHelp, Heading, Input, Label, TextButton } from '@zillow/constellation-10';
import { get } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { MAX_DOG_BREED_LENGTH, MAX_DOG_WEIGHT_LBS_LENGTH } from '../../../../constants/form';

export const DOGS_FIELD_ARRAY_NAME = 'pets.dogs';

export function DogEdit({ index, onDelete }: { index: number; onDelete: () => void }) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const title = `Dog #${index + 1}`;
  const weightFieldName = `${DOGS_FIELD_ARRAY_NAME}[${index}].weightLbs`;
  const breedFieldName = `${DOGS_FIELD_ARRAY_NAME}[${index}].breed`;
  const errorWeight = get(errors, weightFieldName);
  const errorBreed = get(errors, breedFieldName);

  return (
    <Card
      elevated={false}
      outlined
      aria-label={title}
      css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
    >
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Heading level={3} textStyle="heading-xs" css={{ color: 'text.subtle' }}>
          {title}
        </Heading>
        <TextButton onClick={onDelete} type="button" aria-label={`Delete ${title}`}>
          Delete
        </TextButton>
      </Box>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: { base: '1fr', lg: 'repeat(2, 1fr)' },
          gap: 'default',
          alignItems: 'start',
        }}
      >
        <Controller
          name={weightFieldName}
          render={({ field: { onChange, onBlur, value, name, ref } }) => (
            <FormField
              label={<Label>Weight (lbs)</Label>}
              control={
                <Input
                  ref={ref}
                  name={name}
                  inputMode="numeric"
                  value={value ?? ''}
                  onBlur={onBlur}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(parseDogWeight(e.target.value))}
                />
              }
              description={errorWeight?.message ? <FormHelp>{String(errorWeight.message)}</FormHelp> : undefined}
              error={!!errorWeight}
            />
          )}
        />
        <FormField
          label={<Label>Breed</Label>}
          control={<Input maxLength={MAX_DOG_BREED_LENGTH} {...register(breedFieldName)} />}
          description={errorBreed?.message ? <FormHelp>{String(errorBreed.message)}</FormHelp> : undefined}
          error={!!errorBreed}
        />
      </Box>
    </Card>
  );
}

function parseDogWeight(value: string) {
  const digits = value.replace(/[^0-9]/g, '').substring(0, MAX_DOG_WEIGHT_LBS_LENGTH);
  return digits ? Number(digits) : null;
}
